import { useCallback, useEffect, useRef, useState } from "react";
import { getPlayerAvatarAudioSource, getPlayerInfo } from "../../utils/component-utils";
import { updateAudioSettings } from "../../update-audio-settings";
import {
  DEFAULT_VOLUME_BAR_MULTIPLIER,
  getAvatarVolumePref,
  updateAvatarVolumesPref
} from "../../utils/avatar-volume-utils";
import {
  isAvatarLocallyMutedByGain,
  resolveGainForMuteToggle,
  resolveInitialAvatarGain,
  sanitizeAvatarGain,
  toLegacyMutedFlag
} from "../../utils/avatar-volume-state-utils";
import { addAvatarVolumeChangedListener, emitAvatarVolumeChanged } from "../../utils/avatar-volume-events";

const PERSIST_GAIN_DEBOUNCE_MS = 300;

const lastNonZeroGainBySession = new Map();

export function useLocalAudio() {
  const volume = window.APP.store.state?.preferences?.globalVoiceVolume;

  const updateLocalAudio = useCallback(value => {
    APP.store.update({
      preferences: {
        globalVoiceVolume: value
      }
    });
  }, []);

  return [volume, updateLocalAudio];
}

function persistAvatarPref(displayName, gainMultiplier) {
  if (!displayName) return;

  const normalizedGain = sanitizeAvatarGain(gainMultiplier);
  const muted = toLegacyMutedFlag(normalizedGain);
  const current = getAvatarVolumePref(displayName);

  if (current?.gainMultiplier === normalizedGain && current?.muted === muted) return;
  updateAvatarVolumesPref(displayName, normalizedGain, muted);
}

function getAvatarAudioContext(personId, displayNameHint) {
  const playerInfo = getPlayerInfo(personId);
  const displayName = playerInfo?.displayName || displayNameHint || null;
  const audioEl = getPlayerAvatarAudioSource(personId);
  const audio = audioEl ? APP.audios.get(audioEl) : null;

  return { displayName, audioEl, audio };
}

function applyRuntimeGain(audioEl, audio, gainMultiplier) {
  if (!audioEl) return;

  const normalizedGain = sanitizeAvatarGain(gainMultiplier);
  let runtimeChanged = false;

  if (APP.gainMultipliers.get(audioEl) !== normalizedGain) {
    APP.gainMultipliers.set(audioEl, normalizedGain);
    runtimeChanged = true;
  }

  // Keep one source of truth for local avatar muting: gain multiplier.
  if (APP.mutedState.has(audioEl)) {
    APP.mutedState.delete(audioEl);
    runtimeChanged = true;
  }

  if (runtimeChanged && audio) {
    updateAudioSettings(audioEl, audio);
  }
}

function readAvatarVolumeState(personId, displayNameHint, options = {}) {
  const { persist = false } = options;
  const { displayName, audioEl, audio } = getAvatarAudioContext(personId, displayNameHint);
  const pref = displayName ? getAvatarVolumePref(displayName) : undefined;
  const runtimeGain = audioEl ? APP.gainMultipliers.get(audioEl) : undefined;

  const gainMultiplier = resolveInitialAvatarGain({
    runtimeGain,
    prefGain: pref?.gainMultiplier,
    prefMutedLegacy: pref?.muted
  });

  if (audioEl) {
    applyRuntimeGain(audioEl, audio, gainMultiplier);
  }

  if (!isAvatarLocallyMutedByGain(gainMultiplier)) {
    lastNonZeroGainBySession.set(personId, gainMultiplier);
  }

  if (persist) {
    persistAvatarPref(displayName, gainMultiplier);
  }

  return {
    gainMultiplier,
    muted: isAvatarLocallyMutedByGain(gainMultiplier)
  };
}

function writeAvatarGain(personId, gainMultiplier, options = {}) {
  const { persist = true, displayNameHint } = options;
  const { displayName, audioEl, audio } = getAvatarAudioContext(personId, displayNameHint);
  const normalizedGain = sanitizeAvatarGain(gainMultiplier);

  if (audioEl) {
    applyRuntimeGain(audioEl, audio, normalizedGain);
  }

  if (!isAvatarLocallyMutedByGain(normalizedGain)) {
    lastNonZeroGainBySession.set(personId, normalizedGain);
  }

  if (persist) {
    persistAvatarPref(displayName, normalizedGain);
  }

  emitAvatarVolumeChanged({ sessionId: personId, gainMultiplier: normalizedGain });

  return {
    gainMultiplier: normalizedGain,
    muted: isAvatarLocallyMutedByGain(normalizedGain)
  };
}

function writeAvatarMuted(personId, muted, options = {}) {
  const { persist = true, displayNameHint } = options;
  const current = readAvatarVolumeState(personId, displayNameHint, { persist: false });
  const { displayName } = getAvatarAudioContext(personId, displayNameHint);
  const pref = displayName ? getAvatarVolumePref(displayName) : undefined;

  const nextGain = resolveGainForMuteToggle({
    nextMuted: !!muted,
    currentGain: current.gainMultiplier,
    lastNonZeroGain: lastNonZeroGainBySession.get(personId),
    prefGain: pref?.gainMultiplier
  });

  return writeAvatarGain(personId, nextGain, { persist, displayNameHint });
}

export function useAllAvatarVolume(people, onMultiplierChanged) {
  const [multipliers, setMultipliers] = useState({});
  const [mutedStates, setMutedStates] = useState({});
  const persistTimeouts = useRef(new Map());

  const resolveDisplayName = useCallback(
    personId => people.find(person => person.id === personId)?.profile?.displayName,
    [people]
  );

  const syncPeopleState = useCallback(
    (persist = false) => {
      if (!people.length) {
        setMultipliers(prev => (Object.keys(prev).length === 0 ? prev : {}));
        setMutedStates(prev => (Object.keys(prev).length === 0 ? prev : {}));
        return;
      }

      const snapshot = new Map();

      people.forEach(person => {
        snapshot.set(person.id, readAvatarVolumeState(person.id, person?.profile?.displayName, { persist }));
      });

      setMultipliers(prev => {
        let changed = Object.keys(prev).length !== people.length;
        const next = {};

        people.forEach(person => {
          const gainMultiplier = snapshot.get(person.id).gainMultiplier;
          next[person.id] = gainMultiplier;
          if (prev[person.id] !== gainMultiplier) {
            changed = true;
          }
        });

        return changed ? next : prev;
      });

      setMutedStates(prev => {
        let changed = Object.keys(prev).length !== people.length;
        const next = {};

        people.forEach(person => {
          const muted = snapshot.get(person.id).muted;
          next[person.id] = muted;
          if (prev[person.id] !== muted) {
            changed = true;
          }
        });

        return changed ? next : prev;
      });
    },
    [people]
  );

  const schedulePersistGain = useCallback(
    (personId, gainMultiplier) => {
      const previousTimeout = persistTimeouts.current.get(personId);
      if (previousTimeout) {
        clearTimeout(previousTimeout);
      }

      const timeoutId = setTimeout(() => {
        writeAvatarGain(personId, gainMultiplier, { persist: true, displayNameHint: resolveDisplayName(personId) });
        persistTimeouts.current.delete(personId);
      }, PERSIST_GAIN_DEBOUNCE_MS);

      persistTimeouts.current.set(personId, timeoutId);
    },
    [resolveDisplayName]
  );

  const updatePersonMultiplier = useCallback(
    (personId, gainMultiplier) => {
      const next = writeAvatarGain(personId, gainMultiplier, {
        persist: false,
        displayNameHint: resolveDisplayName(personId)
      });

      setMultipliers(prev => ({ ...prev, [personId]: next.gainMultiplier }));
      setMutedStates(prev => ({ ...prev, [personId]: next.muted }));
      schedulePersistGain(personId, next.gainMultiplier);

      onMultiplierChanged && onMultiplierChanged(personId, next.gainMultiplier);
    },
    [onMultiplierChanged, resolveDisplayName, schedulePersistGain]
  );

  const updatePersonMuted = useCallback(
    (personId, muted) => {
      const previousTimeout = persistTimeouts.current.get(personId);
      if (previousTimeout) {
        clearTimeout(previousTimeout);
        persistTimeouts.current.delete(personId);
      }

      const next = writeAvatarMuted(personId, muted, {
        persist: true,
        displayNameHint: resolveDisplayName(personId)
      });

      setMutedStates(prev => ({ ...prev, [personId]: next.muted }));
      setMultipliers(prev => ({ ...prev, [personId]: next.gainMultiplier }));
    },
    [resolveDisplayName]
  );

  useEffect(() => {
    syncPeopleState(true);
  }, [syncPeopleState]);

  useEffect(() => {
    return addAvatarVolumeChangedListener(event => {
      const changedSessionId = event?.detail?.sessionId;
      if (!changedSessionId) return;

      const participant = people.find(person => person.id === changedSessionId);
      if (!participant) return;

      const next = readAvatarVolumeState(changedSessionId, participant?.profile?.displayName, { persist: false });

      setMultipliers(prev =>
        prev[changedSessionId] === next.gainMultiplier ? prev : { ...prev, [changedSessionId]: next.gainMultiplier }
      );
      setMutedStates(prev =>
        prev[changedSessionId] === next.muted ? prev : { ...prev, [changedSessionId]: next.muted }
      );
    });
  }, [people]);

  useEffect(() => {
    const timeoutMap = persistTimeouts.current;

    return () => {
      timeoutMap.forEach(timeoutId => clearTimeout(timeoutId));
      timeoutMap.clear();
    };
  }, []);

  return { multipliers, updatePersonMultiplier, mutedStates, updatePersonMuted };
}

export default function useAvatarVolume(sessionId, onMultiplierChanged, displayNameHint) {
  const [multiplier, setMultiplier] = useState(DEFAULT_VOLUME_BAR_MULTIPLIER);
  const [isMuted, setIsMuted] = useState(false);
  const lastReportedMultiplier = useRef(undefined);

  const syncState = useCallback(
    (persist = false) => {
      if (!sessionId) return;

      const next = readAvatarVolumeState(sessionId, displayNameHint, { persist });

      setMultiplier(prev => (prev === next.gainMultiplier ? prev : next.gainMultiplier));
      setIsMuted(next.muted);

      if (onMultiplierChanged && lastReportedMultiplier.current !== next.gainMultiplier) {
        lastReportedMultiplier.current = next.gainMultiplier;
        onMultiplierChanged(next.gainMultiplier);
      }
    },
    [displayNameHint, onMultiplierChanged, sessionId]
  );

  const updateMultiplier = useCallback(
    gainMultiplier => {
      if (!sessionId) return;

      const next = writeAvatarGain(sessionId, gainMultiplier, { persist: true, displayNameHint });
      setMultiplier(next.gainMultiplier);
      setIsMuted(next.muted);

      if (onMultiplierChanged) {
        lastReportedMultiplier.current = next.gainMultiplier;
        onMultiplierChanged(next.gainMultiplier);
      }
    },
    [displayNameHint, onMultiplierChanged, sessionId]
  );

  const updateMuted = useCallback(
    muted => {
      if (!sessionId) return;

      const next = writeAvatarMuted(sessionId, muted, { persist: true, displayNameHint });
      setMultiplier(next.gainMultiplier);
      setIsMuted(next.muted);

      if (onMultiplierChanged) {
        lastReportedMultiplier.current = next.gainMultiplier;
        onMultiplierChanged(next.gainMultiplier);
      }
    },
    [displayNameHint, onMultiplierChanged, sessionId]
  );

  useEffect(() => {
    lastReportedMultiplier.current = undefined;
    syncState(true);
  }, [sessionId, syncState]);

  useEffect(() => {
    return addAvatarVolumeChangedListener(event => {
      if (event?.detail?.sessionId !== sessionId) return;
      syncState(false);
    });
  }, [sessionId, syncState]);

  return [multiplier, updateMultiplier, isMuted, updateMuted];
}
